import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './GutterCreator.scss';
import store from 'store';
import { AppState } from 'reducers';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { getImportedProductsInfo, resetGutteringsCart } from 'lib/communication/orders';
import { CartGroup, GutteringProductCount, GutteringSystemData, ImportedProduct, Product } from 'lib/types';
import { setGutterCreatorFilter, setGuttertoBeUpdated } from 'lib/communication/gutterCreator';
import useCartMessage from 'lib/hooks/useCartMessage';
import { Messages } from 'reducers/data';

export interface ImportedData {
    resetGutteringsCart: false;
    filters: GutteringSystemData;
    order: {
        SymKar: string;
        count: GutteringProductCount[];
    }[]
}

enum GutterMessageTypes {
    Loaded = 'GUTTER_LOADED',
    Resize = 'GUTTER_RESIZE',
    UpdateOrder = 'GUTTER_UPDATE_ORDER',
    Submit = 'GUTTER_SUBMIT_KREATOR',
    Filter = 'GUTTER_FILTER'
}

interface GutterMessage {
    type: string;
    content?: any;
}

export interface DataFromGutterings {
    product: Product;
    quantity: number;
    systemData?: GutteringSystemData;
}

const IDENTIFIER = 'GutterCreator';

const submitKreator = (message: ImportedData, setShowCartMessage: () => void): void => {
    const data: ImportedData = message;
    const systemData = data.filters as GutteringSystemData;
    if (!data.resetGutteringsCart) {
        const products: ImportedProduct[] = _.flatMap(data.order, (product) => _.map(product.count, (productCount: GutteringProductCount) => ({
            symKar: product.SymKar,
            quantity: productCount.count,
            dimension: null,
            additionsSelected: [],
            systemData,
            unit: productCount.unit.unit,
            cartGroup: CartGroup.gutterings
        })));
        if (products.length > 0) getImportedProductsInfo(products);
    } else {
        resetGutteringsCart(systemData);
    }

    setShowCartMessage();
};

const GutterCreator = React.memo(() => {
    const localCart = useSelector(
        (state: AppState) => (state.data.orders.localCart)
    );

    const { setShowCartMessage } = useCartMessage();

    const GutterCreatorURL = useSelector((state: AppState) => state.conf.GutterCreatorURL);
    const gutterToBeUpdated = useSelector((state: AppState) => state.data.gutterCreator.props.toBeUpdated);
    const isOrderFetching = useSelector((state: AppState) => state.data.orders.props.isFetching);
    const [contentRef, setContentRef] = useState<HTMLIFrameElement | null>(null);
    const [isGutteringsDownloaded, setIsGutteringsDownloaded] = useState(false);
    const [contentHeight, setContentHeight] = useState('500px');
    const [gutterFilters, setGutterFilters] = useState<GutteringSystemData>(store.getState().data.gutterCreator.props.filters);

    useEffect(() => {
        if (gutterToBeUpdated) {
            setGutterFilters(store.getState().data.gutterCreator.props.filters);
            setGuttertoBeUpdated(false);
            setIsGutteringsDownloaded(false);
        }
    }, [gutterToBeUpdated]);

    const GutterCreatorDomain = useMemo(() => GutterCreatorURL.split('/').slice(0, 3).join('/'), [GutterCreatorURL]);

    const gutterMessageHandler = useCallback((event: MessageEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.origin !== GutterCreatorDomain) {
            return;
        }

        const { type, content } = event.data as GutterMessage;

        switch (type) {
            case GutterMessageTypes.Loaded:
                setIsGutteringsDownloaded(true);
                break;
            case GutterMessageTypes.Resize:
                setContentHeight(content);
                break;
            case GutterMessageTypes.Submit:
                submitKreator(content, setShowCartMessage(Messages.cartMessage));
                break;
            case GutterMessageTypes.Filter:
                setGutterCreatorFilter(content);
                break;
            default:
                break;
        }
    }, [GutterCreatorDomain]);

    useEffect(() => {
        if (isGutteringsDownloaded && contentRef && contentRef.contentWindow && !isOrderFetching) {
            const order = JSON.stringify(
                _.filter(localCart.productsInLocalCart, (prd) => prd.cartGroup === CartGroup.gutterings).map((p) => ({
                    product_id: p.product.symKar,
                    product_qty: p.quantity,
                    product_unit: p.unit,
                    systemData: p.systemData
                }))
            );
            const msg: GutterMessage = {
                type: GutterMessageTypes.UpdateOrder,
                content: order
            };

            contentRef.contentWindow.postMessage(msg, GutterCreatorDomain);
        }
    }, [isGutteringsDownloaded, localCart, contentRef, GutterCreatorDomain]);

    useEffect(() => {
        window.addEventListener('message', gutterMessageHandler, false);

        return () => { window.removeEventListener('message', gutterMessageHandler, false); };
    }, [GutterCreatorURL, GutterCreatorDomain, gutterMessageHandler]);

    return (
        <div>
            <iframe
                title={IDENTIFIER}
                style={{
                    width: '100%',
                    height: contentHeight,
                    border: 'none'
                }}
                ref={setContentRef}
                src={`${GutterCreatorURL}?mode=ORDER_MODE&origin=${window.origin}` +
                    `&gutter-shape=${gutterFilters.gutterShape}` +
                    `&material=${gutterFilters.material}` +
                    `&grille=${gutterFilters.grille}` +
                    `&tube-shape=${gutterFilters.tubeShape}` +
                    `&size=${gutterFilters.size}` +
                    `&color-name=${gutterFilters.colorName}`}
            />
        </div>
    );
});

export default GutterCreator;
